import { useEffect, useState } from "react";
import { ClassNames } from "@emotion/core";
import { Stack } from "@certa/blocks/thanos";
import {
  Typography,
  TypographyVariants
} from "@certa/catalyst/components/Typography";
import { Image } from "@certa/blocks";
import Maintananceimage from "../../../images/MaintananceImage.png";
import { MaintananceSelectLanguage } from "../SelectLanguage/MaintananceSelectLanguage";
import { useIntl } from "react-intl";
import styled from "@emotion/styled";

type MaintenanceProps = {
  eta: string;
  msg: Record<string, string>;
};

const SECONDS_MS = 1000;

const padSingleDigit = (n: number): string => {
  if (n < 10) {
    return String(n).padStart(2, "0");
  }
  return String(n);
};

export const Maintenance = (props: MaintenanceProps) => {
  const { eta, msg } = props;
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [days, setDays] = useState(0);
  const intl = useIntl();

  // TODO: This default value needs to be updated from useGetPreferredLanguage
  //       when BE starts sending translated messages
  const defaultPrefferedLanguage = "en";
  const [message, setMessage] = useState(msg[defaultPrefferedLanguage]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null;

    if (eta) {
      const etaDate: any = new Date(eta);
      const differenceBetweenDates = () => {
        const dateNow: any = new Date();
        const dateDifference = etaDate - dateNow;
        let seconds = Math.floor(dateDifference / 1000);

        if (dateDifference > 0) {
          let minutes = Math.floor(seconds / 60);
          let hours = Math.floor(minutes / 60);
          const days = Math.floor(hours / 24);

          hours %= 24;
          minutes %= 60;
          seconds %= 60;

          setDays(days);
          setHours(hours);
          setMinutes(minutes);
          setSeconds(seconds);
        } else {
          setDays(0);
          setHours(0);
          setMinutes(0);
          setSeconds(0);
          if (interval)
            //case: eta received is in past.
            clearInterval(interval);
        }
      };
      // on first render.
      differenceBetweenDates();

      // on second render, and onwards
      interval = setInterval(() => {
        differenceBetweenDates();
      }, SECONDS_MS);
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [eta]);

  const daysText = "DAYS";
  const hrsText = "HRS";
  const minText = "MIN";
  const secText = "SEC";
  return (
    <ClassNames>
      {({ cx, css }) => (
        <Stack
          className={css`
            background: var(--neutral-5);
          `}
        >
          <Stack
            direction="vertical"
            align="center"
            className={css`
              position: fixed;
              width: 100%;
              background: var(--neutral-5);
              margin: auto;
              height: 100vh;
            `}
          >
            <Stack
              direction="vertical"
              align="center"
              className={css`
                margin-top: 10%;
              `}
            >
              <Image
                src={Maintananceimage}
                style={{ width: "640px", height: "360px" }}
                alt=""
              />
              <Typography
                variant={TypographyVariants.TITLE_MD}
                className={css`
                  color: var(--neutral-100);
                `}
                style={{ marginTop: "var(--s6)" }}
              >
                {intl.formatMessage({
                  id: "maintanance.heading",
                  defaultMessage: "Under Maintenance"
                })}
              </Typography>
              <Typography
                variant={TypographyVariants.LABEL_SM}
                className={css`
                  max-width: 400px;
                  text-align: center;
                  line-height: var(--s5);
                  color: var(--neutral-70);
                `}
                style={{ marginTop: "var(--s4)" }}
              >
                {message}
              </Typography>
              {days || hours || minutes || seconds ? (
                <Stack gutter="s6" direction="horizontal" justify="center">
                  {days ? (
                    <Stack gap="s2" direction="vertical" align="center">
                      <WrapperStack as="span">
                        {padSingleDigit(days)}
                      </WrapperStack>
                      <Typography
                        variant={TypographyVariants.LABEL_SM}
                        className={css`
                          color: var(--neutral-70);
                          font-weight: 700;
                        `}
                        style={{ marginTop: "var(--s2)" }}
                      >
                        {daysText}
                      </Typography>
                    </Stack>
                  ) : null}
                  <Stack gap="s2" direction="vertical" align="center">
                    <WrapperStack as="span">
                      {padSingleDigit(hours)}
                    </WrapperStack>
                    <Typography
                      variant={TypographyVariants.LABEL_SM}
                      className={css`
                        color: var(--neutral-70);
                        font-weight: 700;
                      `}
                      style={{ marginTop: "var(--s2)" }}
                    >
                      {hrsText}
                    </Typography>
                  </Stack>
                  <Stack gap="s2" direction="vertical" align="center">
                    <WrapperStack as="span">
                      {padSingleDigit(minutes)}
                    </WrapperStack>
                    <Typography
                      variant={TypographyVariants.LABEL_SM}
                      className={css`
                        color: var(--neutral-70);
                        font-weight: 700;
                      `}
                      style={{ marginTop: "var(--s2)" }}
                    >
                      {minText}
                    </Typography>
                  </Stack>
                  <Stack gap="s2" direction="vertical" align="center">
                    <WrapperStack as="span">
                      {padSingleDigit(seconds)}
                    </WrapperStack>
                    <Typography
                      variant={TypographyVariants.LABEL_SM}
                      className={css`
                        color: var(--neutral-70);
                        font-weight: 700;
                      `}
                      style={{ marginTop: "var(--s2)" }}
                    >
                      {secText}
                    </Typography>
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
            <Stack
              gap="s3"
              className={css`
                position: absolute;
                right: 10px;
                top: 10px;
              `}
            >
              <MaintananceSelectLanguage
                languageData={msg}
                onChangeLanguage={(value: string) => setMessage(value)}
              />
            </Stack>
          </Stack>
        </Stack>
      )}
    </ClassNames>
  );
};

const WrapperStack = styled(Typography)`
  width: 57px;
  height: 38px;
  border-radius: 10px;
  background: var(--neutral-0);
  margin: var(--s1) var(--s1) var(--s00) var(--s1);
  justify-content: center;
  align-items: center;
  color: var(--neutral-100);
  font-size: 18px;
  font-weight: 700;
  display: flex;
`;
